import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result.replace(/^data:[a-zA-Z]+\/[a-zA-Z]+;base64,/, "");
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

const PostForm = () => {
  const initialState = {
    title: "",
    caption: "",
    article: "",
    tags: "",
    category: "",
    quote: "",
    newsCode: 1,
    videoFile: null,         // Holds the newly chosen video file (if any)
    videoUrl: "",           // Holds the existing video URL from the server
    thumbnailBase64: "",
    imageFileBase64: "",
    imageFile2Base64: "",
    existingImageUrl: "",
    existingImage2Url: "",
    description: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef();
  const canvasRef = useRef();

  useEffect(() => {
    if (!id) {
      setFormData(initialState);
      return;
    }

    const fetchArticle = async () => {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        navigate("/admin/login");
        return;
      }

      try {
        const response = await axios.get(
          `https://khanehyeghtesad.com:8000/api/article/detail/${id}/`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        const {
          title,
          caption,
          article,
          tags,
          category,
          quote,
          description,
          news_code,
          video_file,
          image_file,
          image_file2,
          thumbnail,
        } = response.data;

        setFormData({
          ...initialState,
          title: title || "",
          caption: caption || "",
          article: article || "",
          tags: Array.isArray(tags) ? tags.join("- ") : tags || "",
          category: Array.isArray(category) ? category.join(", ") : category || "",
          quote: quote || "",
          description: description || "",
          newsCode: news_code || 1,

          // IMPORTANT: Keep the existing video file's URL
          videoUrl: video_file || "",

          existingImageUrl: image_file || "",
          existingImage2Url: image_file2 || "",
          thumbnailBase64: thumbnail || "",
        });
      } catch (err) {
        console.error("Error fetching article details:", err.response || err.message);
        setError("Error fetching article details.");
      }
    };

    fetchArticle();
  }, [id, navigate]);

  const generateThumbnail = (file) => {
    if (!file || !videoRef.current || !canvasRef.current) return;

    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;
    const videoURL = URL.createObjectURL(file);

    videoElement.src = videoURL;
    videoElement.crossOrigin = "anonymous";

    // Wait until metadata is loaded
    videoElement.onloadedmetadata = () => {
      // Jump to second 1 (or whichever frame you want)
      videoElement.currentTime = 1;
    };

    videoElement.onseeked = () => {
      try {
        const ctx = canvasElement.getContext("2d");
        canvasElement.width = videoElement.videoWidth;
        canvasElement.height = videoElement.videoHeight;
        ctx.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);

        const base64Thumbnail = canvasElement
          .toDataURL("image/jpeg")
          .replace(/^data:image\/jpeg;base64,/, "");

        setFormData((prev) => ({
          ...prev,
          thumbnailBase64: base64Thumbnail,
        }));

        URL.revokeObjectURL(videoURL);
      } catch (error) {
        console.error("Error generating thumbnail:", error);
      }
    };

    videoElement.onerror = (error) => {
      console.error("Error loading video:", error);
      alert("Unable to load video for thumbnail generation.");
    };
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;

    // User has chosen a new video, so we store it as a File,
    // and clear out the old videoUrl.
    setFormData((prev) => ({
      ...prev,
      videoFile: file,
      videoUrl: "", // clear existing video URL
      thumbnailBase64: "",
    }));

    generateThumbnail(file);
  };

  const handleImageFileUpload = async (file, type) => {
    if (!file) return;
    try {
      setLoading(true);
      const base64Image = await convertFileToBase64(file);

      setFormData((prev) => {
        if (type === "imageFileBase64") {
          return { ...prev, imageFileBase64: base64Image, existingImageUrl: "" };
        } else if (type === "imageFile2Base64") {
          return { ...prev, imageFile2Base64: base64Image, existingImage2Url: "" };
        }
        return prev;
      });
      setLoading(false);
    } catch (err) {
      console.error("Error processing image:", err);
      setError("Failed to process image. Please try again.");
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const accessToken = localStorage.getItem("access");
    if (!accessToken) {
      navigate("/admin/login");
      return;
    }

    try {
      setLoading(true);

      // Convert the new video file to Base64, if there's a new one
      const base64VideoFile = formData.videoFile
        ? await convertFileToBase64(formData.videoFile)
        : null;

      const payload = {
        category: formData.category.split(",").map((cat) => cat.trim()),
        title: formData.title,
        caption: formData.caption,
        article: formData.article,
        tags: formData.tags.split("-").map((tag) => tag.trim()),
        quote: formData.quote,
        description: formData.description,
        news_code: formData.newsCode,
        
        // If a new video file is chosen, send its base64; otherwise, keep the old videoUrl
        video_file: base64VideoFile || formData.videoUrl || "",
        
        // Same pattern for images
        image_file: formData.imageFileBase64 || formData.existingImageUrl || "",
        image_file2: formData.imageFile2Base64 || formData.existingImage2Url || "",

        thumbnail: formData.thumbnailBase64 || "",
        creator_username: "username",
      };

      if (id) {
        // Edit existing article
        const response = await axios.put(
          `https://khanehyeghtesad.com:8000/api/article/edit/`,
          {
            ...payload,
            article_id: id,
            slug: formData.title.replace(/\s+/g, "-"),
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        alert(response.data.message || "Article updated successfully.");
      } else {
        // Create a new article
        const response = await axios.post(
          `https://khanehyeghtesad.com:8000/api/article/new/`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        alert(
          response.data.slug
            ? `Post created with slug: ${response.data.slug}`
            : "Post created successfully."
        );
      }

      navigate("/admin");
    } catch (err) {
      console.error(
        "Error saving the post:",
        err.response?.data || err.message || "Unknown error"
      );
      alert(
        `Error saving the post: ${
          err.response?.data?.message || "Server error occurred."
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2>{id ? "Edit Post" : "Create New Post"}</h2>
      <form onSubmit={handleSubmit}>
        <div>
          <label>رو تیتر:</label>
          <input
            type="text"
            value={formData.quote}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, quote: e.target.value }))
            }
          />
        </div>

        <div>
          <label>عنوان:</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, title: e.target.value }))
            }
            required
          />
        </div>

        <div>
          <label>لید:</label>
          <input
            type="text"
            value={formData.caption}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, caption: e.target.value }))
            }
          />
        </div>

        <div>
          <label>متن خبر:</label>
          <ReactQuill
            value={formData.article}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, article: value }))
            }
          />
        </div>

        <div>
          <label>کلید واژه‌ها (با یک - از هم جدا کنید):</label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, tags: e.target.value }))
            }
          />
        </div>

        <div>
          <label>گروه:</label>
          <select
            value={formData.category}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, category: e.target.value }))
            }
            required
          >
            <option value="">Select a category</option>
            <option value="اقتصاد ایران">اقتصاد ایران</option>
            {/* <option value="بورس">بورس</option> */}
            {/* <option value="بانک، بیمه و بورس">بانک، بیمه و بورس</option> */}
            <option value="صنعت">صنعت</option>
            <option value="کشاورزی">کشاورزی</option>
            <option value="راه و ساختمان">راه و ساختمان</option>
            <option value="انرژی">انرژی</option>
            <option value="سایر">سایر</option>
          </select>
        </div>

        <div>
          <label>توضیحات:</label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
          />
        </div>

        {/*
          --- VIDEO SECTION ---
          This is the important part to display the existing video or the new one.
        */}
        <div>
          <label>فایل ویدیو:</label>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => handleVideoUpload(e.target.files[0])}
          />

          {/*
            If a new file was chosen, show its preview using createObjectURL;
            otherwise, if there's an existing video URL, show that.
          */}
          {formData.videoFile ? (
            <video width="200" controls>
              <source src={URL.createObjectURL(formData.videoFile)} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : formData.videoUrl ? (
            <video width="200" controls>
              <source src={formData.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : null}
        </div>

        <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
        <video ref={videoRef} style={{ display: "none" }}></video>

        {/* Optional: Show automatically generated thumbnail, if any */}
        <div>
          <label>Generated Thumbnail:</label>
          {formData.thumbnailBase64 && (
            <img
              src={`data:image/jpeg;base64,${formData.thumbnailBase64}`}
              alt="Thumbnail Preview"
              style={{ width: "200px" }}
            />
          )}
        </div>

        {/* IMAGE 1 */}
        <div>
          <label>فایل عکس 1:</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageFileUpload(e.target.files[0], "imageFileBase64")}
          />
          {(formData.imageFileBase64 || formData.existingImageUrl) && (
            <img
              src={
                formData.imageFileBase64
                  ? `data:image/jpeg;base64,${formData.imageFileBase64}`
                  : formData.existingImageUrl
              }
              alt="Image 1 Preview"
              style={{ width: "200px" }}
            />
          )}
        </div>

        {/* IMAGE 2 */}
        <div>
          <label>فایل عکس 2:</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageFileUpload(e.target.files[0], "imageFile2Base64")}
          />
          {(formData.imageFile2Base64 || formData.existingImage2Url) && (
            <img
              src={
                formData.imageFile2Base64
                  ? `data:image/jpeg;base64,${formData.imageFile2Base64}`
                  : formData.existingImage2Url
              }
              alt="Image 2 Preview"
              style={{ width: "200px" }}
            />
          )}
        </div>

        <button type="submit" disabled={loading}>
          {loading ? "Saving..." : id ? "Update Post" : "Create Post"}
        </button>
      </form>
    </div>
  );
};

export default PostForm;
